import { clsx } from 'clsx';
import type { ReactNode } from 'react';

export function Spinner({ className }: { className?: string }) {
  return (
    <svg className={clsx('animate-spin', className)} width="20" height="20" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function PageLoader({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-24 text-ink-400">
      <Spinner className="h-6 w-6" />
      <p className="text-sm">{label}</p>
    </div>
  );
}

export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-ink-200 bg-white/50 px-6 py-16 text-center">
      <p className="font-medium text-ink-700">{title}</p>
      {hint && <p className="max-w-sm text-sm text-ink-400">{hint}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 px-6 py-10 text-center">
      <p className="font-medium text-red-700">Something went wrong</p>
      <p className="mt-1 text-sm text-red-600">{message}</p>
      {onRetry && (
        <button className="btn-outline mt-4" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

const STATUS_TONE: Record<string, string> = {
  // participation
  Planned: 'bg-ink-100 text-ink-600',
  Confirmed: 'bg-blue-100 text-blue-700',
  Ongoing: 'bg-emerald-100 text-emerald-700',
  Completed: 'bg-ink-200 text-ink-700',
  Cancelled: 'bg-red-100 text-red-700',
  // booking
  New: 'bg-blue-100 text-blue-700',
  Contacted: 'bg-amber-100 text-amber-700',
  Processing: 'bg-violet-100 text-violet-700',
  // entity
  active: 'bg-emerald-100 text-emerald-700',
  inactive: 'bg-ink-100 text-ink-500',
  archived: 'bg-ink-200 text-ink-600',
  sold_out: 'bg-red-100 text-red-700',
  moved: 'bg-amber-100 text-amber-700',
  disabled: 'bg-red-100 text-red-700',
};

export function StatusBadge({ status }: { status: string }) {
  return (
    <span className={clsx('badge', STATUS_TONE[status] ?? 'bg-ink-100 text-ink-600')}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

export function Stat({
  label,
  value,
  sub,
  tone = 'default',
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'default' | 'gold' | 'emerald' | 'red';
}) {
  const toneCls = {
    default: 'text-ink-900',
    gold: 'text-gold-600',
    emerald: 'text-emerald-600',
    red: 'text-red-600',
  }[tone];
  return (
    <div className="card p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-ink-400">{label}</p>
      <p className={clsx('mt-1 text-2xl font-semibold', toneCls)}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-ink-400">{sub}</p>}
    </div>
  );
}

export function Pagination({
  page,
  pages,
  onPage,
}: {
  page: number;
  pages: number;
  onPage: (p: number) => void;
}) {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-1 py-3 text-sm text-ink-500">
      <span>
        Page {page} of {pages}
      </span>
      <div className="flex gap-2">
        <button className="btn-outline" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Prev
        </button>
        <button className="btn-outline" disabled={page >= pages} onClick={() => onPage(page + 1)}>
          Next
        </button>
      </div>
    </div>
  );
}
